<template>
  <PickerContainer
    ref="$pickerContainer"
    :type="type"
    :grid-type="viewGridType"
    :is-disabled="isDisabled"
    :popover="popoverProps"
    @open="onOpen"
    @close="onClose"
  >
    <template #default="{ open, close, toggle }">
      <slot :open="open" :close="close" :toggle="toggle">
        <DatepickerInput
          ref="$datepickerInput"
          :model-value="modelValue"
          :type="type"
          :settings="inputProps"
          :is-disabled="isDisabled"
          @click.stop="closeOtherPickers"
          @update:model-value="emit('update:modelValue', $event)"
          @focus="openFromFocus && toggle()"
          @enter="emit('enter', $event)"
          @blur="emit('blur', $event)"
        >
          <TirPmButton
            class="tir-pm-datepicker__input-button"
            v-if="!openFromFocus"
            :size="TirPmButtonSizeEnum.Small"
            :variant="TirPmButtonVariantEnum.LinkState"
            @click="toggle"
          >
            <template #leftSlot>
              <ClockIcon v-if="type === DatepickerTypeEnum.Time" />
              <CalendarIcon :class="{ 'calendar-icon-disabled': isDisabled }" v-else />
            </template>
          </TirPmButton>
          <template #hintMessage>
            <slot name="hintMessage" />
          </template>
        </DatepickerInput>
      </slot>
    </template>
    <template #content>
      <div class="tir-pm-datepicker__header">
        <template v-if="headerButtonText && type !== DatepickerTypeEnum.Month">
          <TirPmButton
            v-if="viewGridType === PickerGridTypeEnum.Years"
            class="tir-pm-datepicker__header-button"
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
            @click="onClickHeaderButton"
          >
            <span v-html="headerButtonText" />
          </TirPmButton>
          <TirPmButton
            v-else
            class="tir-pm-datepicker__header-button"
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
            @click="onClickHeaderButton"
          >
            <span v-html="headerButtonText" />
            <template #rightSlot>
              <ChevronDownIcon />
            </template>
          </TirPmButton>
        </template>
        <div
          class="tir-pm-datepicker__header-right"
          v-if="visibleClockButton || visibleNavigate || headerLabel"
        >
          <TirPmButton
            v-if="visibleClockButton"
            class="tir-pm-datepicker__time-button"
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
            @click="onClickClock"
          >
            <template #leftSlot>
              <ClockIcon />
            </template>
          </TirPmButton>
          <NavigationConrols v-if="visibleNavigate" @back="onClickPrev" @next="onClickNext" />
          <div class="tir-pm-datepicker__header-label" v-if="headerLabel">
            {{ headerLabel }}
          </div>
        </div>
      </div>
      <DatesGrid
        v-if="viewGridType === PickerGridTypeEnum.Dates"
        v-model="datesGridValue"
        :is-week="type === DatepickerTypeEnum.Week"
        :view-year="viewYear"
        :view-month="viewMonth"
      >
        <template #cell="{ cell }">
          <slot name="dayСell" :cell="cell" />
        </template>
        <template #week-cell="{ weekNum }">
          <slot name="weekСell" :weekNum="weekNum" />
        </template>
      </DatesGrid>
      <MonthsGrid
        v-else-if="viewGridType === PickerGridTypeEnum.Months"
        v-model="monthsGridValue"
        :view-year="viewYear"
        :in-monthpicker="type === DatepickerTypeEnum.Month"
      >
        <template #cell="{ cell }">
          <slot name="monthСell" :cell="cell" />
        </template>
      </MonthsGrid>
      <YearsGrid
        v-else-if="viewGridType === PickerGridTypeEnum.Years"
        v-model="yearsGridValue"
        :first-year="firstYear"
        :in-yearpicker="type === DatepickerTypeEnum.Year"
      >
        <template #cell="{ cell }">
          <slot name="yearСell" :cell="cell" />
        </template>
      </YearsGrid>
      <HoursGrid v-else-if="viewGridType === PickerGridTypeEnum.Hours" v-model="hoursGridValue">
        <template #cell="{ cell }">
          <slot name="hourСell" :cell="cell" />
        </template>
      </HoursGrid>
      <MinutesGrid
        v-else-if="viewGridType === PickerGridTypeEnum.Minutes"
        v-model="minutesGridValue"
        :interval="minutesInterval"
      >
        <template #cell="{ cell }">
          <slot name="minuteСell" :cell="cell" />
        </template>
      </MinutesGrid>
    </template>
  </PickerContainer>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, PropType, provide, ref, watch } from "vue";
import { DatepickerTypeEnum, PickerGridTypeEnum } from "./enums";
import {
  DatepickerInput,
  DatesGrid,
  HoursGrid,
  MinutesGrid,
  MonthsGrid,
  NavigationConrols,
  PickerContainer,
  YearsGrid,
} from "./components";
import { TirPmButton, TirPmButtonSizeEnum, TirPmButtonVariantEnum } from "tir-pm-button";
import { ChevronDownIcon, ClockIcon, CalendarIcon } from "tir-style-system/icons/outline";
import { locale } from "./constants";
import { PopoverPositionEnum } from "tir-pm-popover";
import { uuidv4 } from "./utils";
import type { DatepickerInputProps, DatepickerPopoverProps } from "./types";

const emit = defineEmits<{
  /**Событие выбора даты */
  (e: "update:modelValue", value: Date | undefined): void;
  /**Событие enter */
  (e: "enter", isCorrectDate: boolean): void;
  /**Событие блюра */
  (e: "blur", isCorrectDate: boolean): void;
  /**Событие сохранения даты */
  (e: "save"): void;
}>();

const props = defineProps({
  /**Выбранная дата */
  modelValue: { type: Date },
  /**Тип пикера */
  type: {
    type: String as PropType<DatepickerTypeEnum>,
    default: () => DatepickerTypeEnum.Date,
  },
  /**Заблокированно */
  isDisabled: { type: Boolean, default: false },
  /**Закрыть после выбора */
  closeAftetSelection: { type: Boolean, default: true },
  /**Закрывать ли другие открытые пикеры при открытии текущего */
  isCloseOtherPickers: { type: Boolean, default: true },
  /**Пропустить пикер при событии закрытия всех */
  skipCloseOtherPickers: { type: Boolean, default: false },
  /**Интервал минут */
  minutesInterval: { type: Number, default: 15 },
  /**Настройки инпута */
  input: { type: Object as PropType<DatepickerInputProps> },
  /**Настройки поповера */
  popover: { type: Object as PropType<DatepickerPopoverProps> },
});

/**Идентификатор */
const uuid = ref("");
provide("datepicker-uuid", uuid);
/**Ссылка на инпут */
const $datepickerInput = ref<InstanceType<typeof DatepickerInput>>();

/**Контейнер пикера */
const $pickerContainer = ref<InstanceType<typeof PickerContainer>>();
/**Закрыть пикер */
const close = () => $pickerContainer.value?.close();
/**Открыть пикер */
const open = () => $pickerContainer.value?.open();
/**Открыть/закрыть пикер */
const toggle = () => $pickerContainer.value?.toggle();
/**Закрыть все остальные пикеры */
const closeOtherPickers = () => {
  if (props.isCloseOtherPickers) {
    document.dispatchEvent(
      new CustomEvent("closeOtherPickers", {
        detail: { ignore: [uuid.value] },
      }),
    );
  }
};
/**При событии закрытия всех пикеров */
const onCloseOtherPickers = (event: any) => {
  // Пропустить текущий
  const isSkip = props.skipCloseOtherPickers || event.detail?.ignore?.includes(uuid.value);
  if (!isSkip) close();
};

/**Пропсы поповера */
const popoverProps = computed(() => {
  const defaultProps: DatepickerPopoverProps = {
    position: PopoverPositionEnum.Bottom,
    isNoTeleport: false,
  };

  return props.popover ? Object.assign(defaultProps, props.popover) : defaultProps;
});
/**Пропсы инпута */
const inputProps = computed(() => {
  const defaultProps: DatepickerInputProps = {
    isSmall: false,
    isShowHint: false,
    isWithHint: false,
    isWithError: false,
    isWithSuccess: false,
  };
  return props.input ? Object.assign(defaultProps, props.input) : defaultProps;
});

/**
 * * Получчить тип сетки на основании типа датапикера
 * @param type
 */
const getGridTypeByDatepickerType = (type: DatepickerTypeEnum) => {
  return {
    [DatepickerTypeEnum.Date]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.DateTime]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.Week]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.Month]: PickerGridTypeEnum.Months,
    [DatepickerTypeEnum.Year]: PickerGridTypeEnum.Years,
    [DatepickerTypeEnum.Time]: PickerGridTypeEnum.Hours,
  }[type];
};
/**Отображаемый тип пикера */
const viewGridType = ref(getGridTypeByDatepickerType(props.type));
/**Получить отображаемый год */
const getViewYear = () => (props.modelValue ?? new Date()).getFullYear();
/**Получить отображаемый месяц */
const getViewMonth = () => (props.modelValue ?? new Date()).getMonth();
/**Отображаемый год */
const viewYear = ref<number>(getViewYear());
/**Отображаемый месяц */
const viewMonth = ref<number>(getViewMonth());
/**Открытие по фокусу */
const openFromFocus = computed(() =>
  [DatepickerTypeEnum.Month, DatepickerTypeEnum.Year, DatepickerTypeEnum.Week].includes(props.type),
);
/**Значение для DatesGrid */
const datesGridValue = computed({
  get() {
    return props.modelValue;
  },
  async set(value) {
    emit("update:modelValue", value);
    emit("save");
    if (props.closeAftetSelection && props.type !== DatepickerTypeEnum.DateTime) {
      await nextTick();
      close();
    }
  },
});
/**Значение для MonthsGrid */
const monthsGridValue = computed({
  get() {
    switch (props.type) {
      case DatepickerTypeEnum.Month:
        return props.modelValue?.getMonth();
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        return viewMonth.value;
    }
  },
  async set(value) {
    if (value === undefined) return;

    switch (props.type) {
      case DatepickerTypeEnum.Month: {
        const date = new Date(props.modelValue ?? new Date());
        date.setMonth(value);
        emit("update:modelValue", date);
        emit("save");
        await nextTick();
        if (props.closeAftetSelection) close();
        break;
      }
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        viewMonth.value = value;
        viewGridType.value = PickerGridTypeEnum.Dates;
    }
  },
});
/**Значение для YearsGrid */
const yearsGridValue = computed({
  get() {
    switch (props.type) {
      case DatepickerTypeEnum.Year:
        return props.modelValue?.getFullYear();
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.Month:
      case DatepickerTypeEnum.DateTime:
        return viewYear.value;
    }
  },
  async set(value) {
    if (value === undefined) return;

    switch (props.type) {
      case DatepickerTypeEnum.Year: {
        const date = new Date(props.modelValue ?? new Date());
        date.setFullYear(value);
        emit("update:modelValue", date);
        emit("save");
        await nextTick();
        if (props.closeAftetSelection) close();
        break;
      }
      case DatepickerTypeEnum.Month:
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        viewYear.value = value;
        viewGridType.value = PickerGridTypeEnum.Months;
    }
  },
});
/**Значение для HoursGrid */
const hoursGridValue = computed({
  get() {
    return props.modelValue?.getHours();
  },
  set(value) {
    if (value === undefined) return;
    const date = new Date(props.modelValue ?? new Date());
    date.setHours(value);
    emit("update:modelValue", date);
    emit("save");
    viewGridType.value = PickerGridTypeEnum.Minutes;
  },
});
/**Значение для MinutesGrid */
const minutesGridValue = computed({
  get() {
    return props.modelValue?.getMinutes();
  },
  async set(value) {
    if (value === undefined) return;
    const date = new Date(props.modelValue ?? new Date());
    date.setMinutes(value);
    emit("update:modelValue", date);
    emit("save");
    await nextTick();
    if (props.closeAftetSelection) close();
    else viewGridType.value = getGridTypeByDatepickerType(props.type);
  },
});
/**Получить первый год в списке */
const getFirstYear = () => {
  const startYear = 2000;
  const toViewYear = viewYear.value;
  const countElementInList = 12;

  // Вычисляем смещение, округляя вверх или вниз, чтобы получить кратный countElementInList
  const offset = Math.floor((toViewYear - startYear) / countElementInList) * countElementInList;

  return startYear + offset;
};

/**Первый отображаемый год */
const firstYear = ref<number>(getFirstYear());

/**Текст кнопки в шапке*/
const headerButtonText = computed(() => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Dates:
      return `${locale.monthNames[viewMonth.value]} ${viewYear.value}`;
    case PickerGridTypeEnum.Months:
      return `${viewYear.value}`;
    case PickerGridTypeEnum.Years:
      return `<span class='years-range-text'>${firstYear.value} - ${firstYear.value + 11}</span>`;
    case PickerGridTypeEnum.Hours:
    case PickerGridTypeEnum.Minutes:
      const date = new Date(props.modelValue ?? new Date());
      return props.type !== DatepickerTypeEnum.Time
        ? `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}.${date.getFullYear()}`
        : undefined;
  }
});
/**Видимость кнопки с часами */
const visibleClockButton = computed(
  () =>
    props.type === DatepickerTypeEnum.DateTime && viewGridType.value === PickerGridTypeEnum.Dates,
);
/**Видимость навигации */
const visibleNavigate = computed(() =>
  [PickerGridTypeEnum.Dates, PickerGridTypeEnum.Years].includes(viewGridType.value),
);
/**Лейбл в хедере */
const headerLabel = computed(() => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Hours:
      return "часы";
    case PickerGridTypeEnum.Minutes:
      return "минуты";
  }
});

/**Клик на кнопку с часами */
const onClickClock = () => {
  viewGridType.value = PickerGridTypeEnum.Hours;
};

/**При клике на кнопку вперед */
const onClickNext = () => {
  if (viewGridType.value === PickerGridTypeEnum.Dates) toNextMonth();
  else if (viewGridType.value === PickerGridTypeEnum.Years) toNextYearList();
};

/**При клике на кнопку назад */
const onClickPrev = () => {
  if (viewGridType.value === PickerGridTypeEnum.Dates) toPrevMonth();
  else if (viewGridType.value == PickerGridTypeEnum.Years) toPrevYearList();
};

/**Перейти к следующему месяцу */
const toNextMonth = () => {
  if (viewMonth.value === 11) {
    viewYear.value++;
    viewMonth.value = 0;
    return;
  }
  viewMonth.value++;
};
/**Перейти к предыдущему месяцу */
const toPrevMonth = () => {
  if (viewMonth.value === 0) {
    viewYear.value--;
    viewMonth.value = 11;
    return;
  }
  viewMonth.value--;
};

/**Перейти к следующему списку годов */
const toNextYearList = () => {
  firstYear.value += 12;
};

/**Перейти к предыдущему списку годов */
const toPrevYearList = () => {
  firstYear.value -= 12;
};

/**При открытии */
const onOpen = () => {
  viewYear.value = getViewYear();
  viewMonth.value = getViewMonth();
  firstYear.value = getFirstYear();
};
/**При закрытии */
const onClose = () => {
  viewGridType.value = getGridTypeByDatepickerType(props.type);
};

const onClickHeaderButton = () => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Dates:
      viewGridType.value = PickerGridTypeEnum.Months;
      break;
    case PickerGridTypeEnum.Months:
      viewGridType.value = PickerGridTypeEnum.Years;
      firstYear.value = getFirstYear();
      break;
    case PickerGridTypeEnum.Hours:
    case PickerGridTypeEnum.Minutes:
      viewGridType.value = PickerGridTypeEnum.Dates;
  }
};

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      viewYear.value = getViewYear();
      viewMonth.value = getViewMonth();
    }
  },
);

onMounted(() => {
  uuid.value = uuidv4();
  document.addEventListener("closeOtherPickers", onCloseOtherPickers);
});
onUnmounted(() => {
  document.removeEventListener("closeOtherPickers", onCloseOtherPickers);
});

defineExpose({
  open,
  close,
  toggle,
  $datepickerInput,
});
</script>

<style lang="scss" scoped>
.calendar-icon-disabled {
  color: var(--text-tertiary, #93969b);
}
.tir-pm-datepicker {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--tir-pm-datepicker__header-mb);
  }
  &__header-right {
    display: flex;
    align-items: center;
  }
  &__time-button {
    margin-right: var(--tir-pm-datepicker__header-time-mr);
  }
  &__header-label {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.25rem;
  }
}
</style>

<style lang="scss">
.tir-pm-datepicker {
  &__header-button {
    .years-range-text {
      color: var(--text-tertiary, #93969b);
    }
  }
  &__input-button {
    .button__left-slot {
      width: var(--tir-pm-datepicker__input-icon-size) !important;
      height: var(--tir-pm-datepicker__input-icon-size) !important;
    }
    padding: var(--tir-pm-datepicker__input-button-padding) !important;
  }
}
</style>
